import { z } from 'zod';
import { isoDate, uuid } from './common';

/**
 * Templates, and meetings written up with them (2026-09-25, PRD 61).
 *
 * Aleksandr at the AI Talks: *"can it be prompted to send specific summaries
 * by your prompt to a specific place?"* A template is what the write-up is
 * for plus its sections, each with instructions for what goes under it. A
 * write-up sits BESIDE the full note, never in place of it.
 */

/** One heading of the write-up, and what the model is told to put under it. */
export const templateSection = z.object({
  title: z.string().trim().min(1).max(120),
  prompt: z.string().trim().max(2000),
});
export type TemplateSection = z.infer<typeof templateSection>;

export const templateKind = z.enum(['stock', 'personal', 'workspace']);
export type TemplateKind = z.infer<typeof templateKind>;

export const noteTemplate = z.object({
  /** `stock:<key>` for a built-in one, else the row's id. */
  id: z.string(),
  kind: templateKind,
  name: z.string(),
  description: z.string(),
  /** What the whole write-up is for: its reader, its length, its tone. */
  instructions: z.string(),
  sections: z.array(templateSection),
  /** Who made it; null for a built-in one. */
  owner: z.object({ id: uuid, name: z.string().nullable(), email: z.string() }).nullable(),
  /** Its owner, or an administrator of the workspace it is shared with. */
  canEdit: z.boolean(),
  updatedAt: isoDate.nullable(),
});
export type NoteTemplate = z.infer<typeof noteTemplate>;

export const templateBody = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(240).default(''),
  instructions: z.string().trim().max(4000).default(''),
  sections: z.array(templateSection).min(1).max(12),
  /** Only me, or everyone in my workspace. */
  scope: z.enum(['personal', 'workspace']),
});
export type TemplateBody = z.infer<typeof templateBody>;

export const writeupStatus = z.enum(['writing', 'ready', 'failed']);

/** A meeting written up with a template — beside the full note, never instead of it. */
export const writeup = z.object({
  id: uuid,
  templateId: z.string(),
  /** As it was called when this was written. */
  templateName: z.string(),
  status: writeupStatus,
  /** Markdown, once written. */
  body: z.string().nullable(),
  /** Why it could not be written, in words for a person. */
  error: z.string().nullable(),
  /** Who asked for it; null when a series rule wrote it after the meeting. */
  askedBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
  /** Every channel it went to, newest last. */
  posts: z.array(z.object({ channelName: z.string(), at: isoDate })),
  writtenAt: isoDate.nullable(),
  createdAt: isoDate,
  /** Whoever asked for it, or whoever manages the meeting. */
  canDelete: z.boolean(),
  /**
   * Changed by hand since it was written, and by whom — null while it is the
   * model's own text. Writing it again (Rewrite, or the same template) clears
   * both, so the next reader is never told a model's text is somebody's.
   */
  editedAt: isoDate.nullable(),
  editedBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
  /** Whoever asked for it, or whoever may edit the meeting's note. */
  canEdit: z.boolean(),
});
export type Writeup = z.infer<typeof writeup>;

/** The write-up as somebody changed it — Markdown, as it is written. */
export const writeupEditBody = z.object({ body: z.string().max(100_000) });
export type WriteupEditBody = z.infer<typeof writeupEditBody>;

export const applyTemplateBody = z.object({ templateId: z.string().min(1).max(80) });
export type ApplyTemplateBody = z.infer<typeof applyTemplateBody>;

export const writeupSlackBody = z.object({
  channelId: z.string().min(1).max(64),
  channelName: z.string().min(1).max(120),
});
export type WriteupSlackBody = z.infer<typeof writeupSlackBody>;

/** A series rule: after every meeting, write it up with this template — and post it here. */
export const seriesWriteupRule = z.object({
  templateId: z.string().min(1).max(80),
  /** Null: written, not posted. */
  slackChannelId: z.string().min(1).max(64).nullable(),
  slackChannelName: z.string().min(1).max(120).nullable(),
});
export type SeriesWriteupRule = z.infer<typeof seriesWriteupRule>;
